import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { withRealizationScope } from "../hooks/initHook";
import { useRealizationContext } from "../reducers/utilities";
import { csvParseLine } from "../utils/csvUtils";
import { ReactNodeDialog } from "../utils/dialogUtlities";
import { writeField } from "../utils/objectUtilities";
import Button from "./Button";
import { customFieldFromString } from "./CustomCtrl";
import FileInput from "./FileInput";
import { Col, Row } from "./Grid";
import Icon from "./Icon";
import useMessageDisplay from "./MessageDisplay";

const CSVUploadButton = ({ columns, children, onClick, args, hideLabel = false, ...props }) => {
    const { t, i18n } = useTranslation();
    const [showUpload, setShowUpload] = useState(false);
    const [fileData, setFileData] = useState();
    const realizationContext = useRealizationContext();
    const { showError } = useMessageDisplay();

    const handleCSV = useCallback((fileData) => {
        const commaIdx = fileData.data.indexOf(',');
        if (commaIdx === -1) {
            showError(t('csvProcessingError'), t('invalidFile'));
            setFileData();
            return;
        }

        const header = fileData.data.slice(0, commaIdx);
        const rawData = fileData.data.slice(commaIdx + 1);

        const match = header.match(
            /^data:(?<mime>[^;,]+)?(?:;charset=(?<charset>[^;,]+))?(?:;(?<base64>base64))?$/
        );

        if (!match) {
            showError(t('csvProcessingError'), t('invalidFile'));
            setFileData();
            return;
        }

        const { mime = 'text/plain', charset = 'US-ASCII', base64 } = match.groups;
        if (!['text/plain','text/csv'].includes(mime)) {
            showError(t('csvProcessingError'), t('invalidFile'));
            setFileData();
            return;
        }

        let bytes;
        if (base64) {
            if (typeof Uint8Array.fromBase64 === 'function') {
                bytes = Uint8Array.fromBase64(rawData);
            } else {
                const binary = atob(rawData);
                bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
            }
        } else {
            const decoded = decodeURIComponent(rawData);
            bytes = new TextEncoder().encode(decoded);
        }
        setFileData();

        const text = new TextDecoder(charset).decode(bytes);

        const lines = text?.split(/\r?\n/)?.filter(line => line?.length > 0);
        if (lines?.length > 0) {
            withRealizationScope(realizationContext, async () => {
                let toAdd = [];
                let errors = [];
                for (let i = 0; i < lines.length; ++i) {
                    const line = csvParseLine(lines[i]);
                    if (line.length !== columns.length) {
                        errors.push(t('lineLengthError', { line: i + 1 }));
                        continue;
                    }
                    let obj = {};
                    for (let j = 0; j < line.length; ++j) {
                        const text = line[j];
                        const col = columns[j];
                        try {
                            writeField(obj, col.field, await customFieldFromString(col, text, realizationContext, i18n.resolvedLanguage, args));
                        } catch (error) {
                            errors.push(t('lineError', { line: i + 1, column: j + 1, field: col.title, error }));
                            obj = null; break;
                        }
                    }
                    if (obj) {
                        toAdd.push(obj);
                    }
                }
                if (errors.length > 0) {
                    showError(t('csvProcessingError'), <><ul>{errors.slice(0, 20).map(e => <li>{e}</li>)}</ul>{errors.length > 20 ? t('moreEllipses') : ''}</>);
                    return;
                }
                onClick(toAdd);
            })
        }
    }, [columns, realizationContext, i18n, showError]);

    return <>
        { showUpload && <ReactNodeDialog showModal={showUpload} title={t('csvUpload')} invalid={!fileData} size="lg" onSave={() => { setShowUpload(false); handleCSV(fileData)}} onClose={() => { setFileData(); setShowUpload(false); }}>
            <Row>
                <Col>
                    {t('csvUploadInstruction')}
                    <ol>
                        {columns.map(col => <li>{col.title ?? col.label} - {col.type}</li>)}
                    </ol>
                </Col>
            </Row>
            <Row>
                <Col>
                    <FileInput value={fileData} placeholder={t('csvFile')} required onChange={setFileData}></FileInput>
                </Col>
            </Row>
        </ReactNodeDialog>}
        <Button {...props} onClick={() => setShowUpload(true)}>
            {children}
            {!children && <Icon intent="upload"></Icon>}
            {!children && !hideLabel && t('uploadFile')}
        </Button>
    </>
};

export default CSVUploadButton;